import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";

const TICK_MS = 60_000;

export const TimeContext = createContext(null);

export function TimeProvider({ children }) {
  const [now, setNow] = useState(() => Date.now());

  const subscribersRef = useRef(0);
  const intervalIdRef = useRef(null);
  const timeoutIdRef = useRef(null);
  const pausedRef = useRef(false);

  const clearTimers = useCallback(() => {
    if (intervalIdRef.current !== null) {
      clearInterval(intervalIdRef.current);
      intervalIdRef.current = null;
    }
    if (timeoutIdRef.current !== null) {
      clearTimeout(timeoutIdRef.current);
      timeoutIdRef.current = null;
    }
  }, []);

  const startTimer = useCallback(() => {
    if (intervalIdRef.current !== null || timeoutIdRef.current !== null) return;
    const tick = () => setNow(Date.now());
    intervalIdRef.current = setInterval(tick, TICK_MS);
    const msToNextMinute = TICK_MS - (Date.now() % TICK_MS);
    timeoutIdRef.current = setTimeout(() => {
      tick();
      if (intervalIdRef.current !== null) clearInterval(intervalIdRef.current);
      intervalIdRef.current = setInterval(tick, TICK_MS);
      timeoutIdRef.current = null;
    }, msToNextMinute);
  }, []);

  const subscribe = useCallback(() => {
    subscribersRef.current += 1;
    if (subscribersRef.current === 1 && !pausedRef.current) startTimer();
    return () => {
      subscribersRef.current = Math.max(0, subscribersRef.current - 1);
      if (subscribersRef.current === 0) clearTimers();
    };
  }, [startTimer, clearTimers]);

  const start = useCallback(() => {
    pausedRef.current = false;
    if (subscribersRef.current > 0) startTimer();
  }, [startTimer]);

  const stop = useCallback(() => {
    pausedRef.current = true;
    clearTimers();
  }, [clearTimers]);

  useEffect(() => () => clearTimers(), [clearTimers]);

  const value = useMemo(
    () => ({ now, subscribe, start, stop }),
    [now, subscribe, start, stop]
  );

  return <TimeContext.Provider value={value}>{children}</TimeContext.Provider>;
}

TimeProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function useTimeController() {
  const ctx = useContext(TimeContext);
  return useMemo(
    () => ({
      start: ctx?.start ?? (() => {}),
      stop: ctx?.stop ?? (() => {}),
    }),
    [ctx]
  );
}
